import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
// import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Header from './components/global/Header';
import Footer from './components/global/Footer';
import MainContent from './components/global/MainContent';
import AdminLayout from './components/admin/AdminLayout';
import LoadingSpinner from './components/global/LoadingSpinner';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ForgotPasswordForm from './components/auth/ForgotPasswordForm';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import VerifyEmail from './components/auth/VerifyEmail';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { ACCESS_GROUPS } from './types/user';
import UnauthorizedPage from './components/auth/UnauthorizedPage';
import NetworkStatus from './components/auth/NetworkStatus';
import ErrorBoundary from './components/auth/ErrorBoundary';
import OfflineBanner from './components/global/OfflineBanner';

// Import CSS files
import './styles/frontend-global.css';
import './styles/auth-global.css';
import './styles/admin-global.css';

// Lazy load valid page components
const HomePage = lazy(() => import('./components/frontend/HomePage'));
const MapServices = lazy(() => import('./components/frontend/MapServices'));
const Signalment = lazy(() => import('./components/frontend/Signalment'));
const VictimConfirmation = lazy(() => import('./components/frontend/VictimConfirmation'));
const RequestHelp = lazy(() => import('./components/frontend/RequestHelp'));
const AboutPage = lazy(() => import('./components/frontend/AboutPage'));
const FaqPage = lazy(() => import('./components/frontend/FaqPage'));
const Profesionales = lazy(() => import('./components/frontend/Profesionales'));
const AllServicesPage = lazy(() => import('./components/frontend/AllServicesPage'));
const InteractiveMap = lazy(() => import('./components/frontend/InteractiveMap'));
const VictimServicesResults = lazy(() => import('./components/frontend/VictimServicesResults'));
const ActorDetailsPage = lazy(() => import('./components/frontend/ActorDetailsPage'));
const GoogleMapExtractor = lazy(() => import('./components/frontend/GoogleMapExtractor'));

// Admin Components Lazy Load
const Dashboard = lazy(() => import('./components/admin/Dashboard'));
const CategoriesManagement = lazy(() => import('./components/admin/CategoriesManagement'));
const ServiceProvidersManagement = lazy(() => import('./components/admin/ServiceProvidersManagement'));
const CitiesManagement = lazy(() => import('./components/admin/CitiesManagement'));
const SignalementsManagement = lazy(() => import('./components/admin/SignalementsManagement'));
const UsersManagement = lazy(() => import('./components/admin/UsersManagement'));
const RoleManagement = lazy(() => import('./components/admin/RoleManagement'));
const ServicesManagement = lazy(() => import('./components/admin/ServicesManagement'));
const ServiceDetails = lazy(() => import('./components/admin/ServiceDetails'));
const AddService = lazy(() => import('./components/admin/AddService'));
const EditService = lazy(() => import('./components/admin/EditService'));
const DataExport = lazy(() => import('./components/admin/DataExport'));
const SurveysManagement = lazy(() => import('./components/admin/SurveysManagement'));
const SurveyDetails = lazy(() => import('./components/admin/SurveyDetails'));

// Admin Main Content
// const AdminMainContent = lazy(() => import('./components/admin/AdminMainContent')); // Not used in routes currently

// Configure React Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days (replacement for cacheTime in v5)
      staleTime: 1000 * 60 * 60 * 24, // 24 hours
      retry: 1,
    },
  },
});

// Create Persister (LocalStorage)
// const persister = createSyncStoragePersister({
//   storage: window.localStorage,
// });

// Placeholder components (will be created next) - KEEPING AS IS for now, just static
const LoginPage = () => <LoginForm />;

const RegisterPage = () => <RegisterForm />;

const ForgotPasswordPage = () => <ForgotPasswordForm />;

const ResetPasswordPage = () => <ResetPasswordForm />;

const VerifyEmailPage = () => <VerifyEmail />;

function isAdminDomain(): boolean {
  const adminDomain = process.env.REACT_APP_ADMIN_DOMAIN;
  if (!adminDomain) return false;
  return window.location.hostname === adminDomain;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <Router>
                <div className="App">
                  <NetworkStatus />
                  <OfflineBanner />
                  <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                      {isAdminDomain() ? (
                        <>
                          {/* Auth routes */}
                          <Route path="/login" element={<LoginPage />} />
                          <Route path="/register" element={<RegisterPage />} />
                          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
                          <Route path="/unauthorized" element={<UnauthorizedPage />} />

                          {/* Redirect root to admin dashboard */}
                          <Route path="/" element={<Navigate to="/admin" replace />} />

                          {/* Admin routes - Protected */}
                          <Route path="/admin" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <Dashboard />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/categories" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <CategoriesManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/service-providers" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <ServiceProvidersManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/cities" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <CitiesManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/signalements" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <SignalementsManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/surveys" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <SurveysManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />
                          <Route path="/admin/surveys/:id" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <SurveyDetails />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/services" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <ServicesManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />
                          <Route path="/admin/services/new" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <AddService />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />
                          <Route path="/admin/services/:id/edit" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <EditService />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />
                          <Route path="/admin/services/:id" element={
                            <ProtectedRoute>
                              <AdminLayout>
                                <ServiceDetails />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/users" element={
                            <ProtectedRoute requiredRoles={[...ACCESS_GROUPS.USER_MANAGERS]}>
                              <AdminLayout>
                                <UsersManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/roles" element={
                            <ProtectedRoute requiredRoles={[...ACCESS_GROUPS.SUPER_ADMIN_ONLY]}>
                              <AdminLayout>
                                <RoleManagement />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          <Route path="/admin/data-export" element={
                            <ProtectedRoute requiredRoles={[...ACCESS_GROUPS.SUPER_ADMIN_ONLY]}>
                              <AdminLayout>
                                <DataExport />
                              </AdminLayout>
                            </ProtectedRoute>
                          } />

                          {/* Catch-all on admin domain */}
                          <Route path="*" element={<Navigate to="/admin" replace />} />
                        </>
                      ) : (
                        <>
                          {/* Frontend routes */}
                          <Route path="/" element={
                            <>
                              <Header variant="frontend" />
                              <MainContent variant="frontend">
                                <HomePage />
                              </MainContent>
                              <Footer variant="frontend" />
                            </>
                          } />

                          <Route path="/services" element={
                            <>
                              <Header variant="frontend" />
                              <MapServices />
                            </>
                          } />

                          <Route path="/services/:id" element={<ActorDetailsPage />} />

                          <Route path="/interactive-map" element={
                            <>
                              <Header variant="frontend" />
                              <InteractiveMap />
                              <Footer variant="frontend" />
                            </>
                          } />

                          <Route path="/espace-professionnels" element={
                            <>
                              <Header variant="frontend" />
                              <MainContent variant="frontend">
                                <Profesionales />
                              </MainContent>
                              <Footer variant="frontend" />
                            </>
                          } />

                          <Route path="/about" element={
                            <>
                              <Header variant="frontend" />
                              <MainContent variant="frontend">
                                <AboutPage />
                              </MainContent>
                              <Footer variant="frontend" />
                            </>
                          } />

                          <Route path="/all-services" element={
                            <>
                              <Header variant="frontend" />
                              <MainContent variant="frontend">
                                <AllServicesPage />
                              </MainContent>
                              <Footer variant="frontend" />
                            </>
                          } />

                          <Route path="/faq" element={
                            <>
                              <Header variant="frontend" />
                              <MainContent variant="frontend">
                                <FaqPage />
                              </MainContent>
                              <Footer variant="frontend" />
                            </>
                          } />

                          <Route path="/signalment" element={<Signalment />} />

                          <Route path="/victim-confirmation" element={<VictimConfirmation />} />

                          <Route path="/request-help" element={<RequestHelp />} />

                          <Route path="/victim-services-results" element={
                            <>
                              <Header variant="frontend" />
                              <VictimServicesResults />
                              <Footer variant="frontend" />
                            </>
                          } />

                          <Route path="/google-map" element={<GoogleMapExtractor />} />

                          {/* Catch-all on main domain */}
                          <Route path="*" element={<Navigate to="/" replace />} />
                        </>
                      )}
                    </Routes>
                  </Suspense>
                </div>
              </Router>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
